/* SKROLLEX 3.0

* Now with less jQuery.
* Authored by Jacob Fisher and David Robbins, in the year of our Musk, 2018

*/

// import { SkrollexChildBase, SkrollexChildGroup } from './children';

import { bindEvents } from '../helpers';

export default class Skrollex {

	constructor(container = document.body, options) {
		this.el = {
			container
		};

		// default settings

		this.defaults = {
			autoEnable: true,

			scrollObjectSelector:		'[data-skrollex]',
			scrollObjectChildSelector: 	'[data-skrollex-child]',

			classInit:					'sk-init',
			classShowFirst:				'sk-show-first',
			classShowStart:				'sk-show-start',
			classShow:					'sk-show',
			classShowComplete:			'sk-show-complete',
			classHideStart:				'sk-hide-start',
			classHide:					'sk-hide',
			classInViewport:			'sk-viewport-in',
			classOutViewport:			'sk-viewport-out',

			attributeUniqueId:			'data-skrollex-id',
			attributeGroup:				'data-skrollex-group',
			attributeStagger:			'data-skrollex-stagger',

			showGutterTop:				'0px',
			showGutterBottom:			'0%',
			observerThreshold:			[0.01],
			stagger:					0.1,
			maxStaggerElementOffset:	10,

			// callbacks

			onElementInit: () => {},
			onElementShow: () => {},
			onElementHide: () => {},
			onElementViewportEnter: () => {},
			onElementViewportLeave: () => {}
		};

		// merge initialized options with defaults

		this.options = this._merge(this.defaults, options);

		// bind event scope

		this.events = bindEvents(this);

		// class variables groups

		this.counters = {
			uid: 0
		};

		this.flags = {
			isEnabled: false,
			isListening: false
		};

		// TODO: need mutation observer?

		this.observers = {
			intersection: new IntersectionObserver(this.events._onObserverUpdate, {
				rootMargin: `${this.options.showGutterTop} 0px ${this.options.showGutterBottom} 0px`,
				threshold: this.options.observerThreshold
			})
		};

		this.scrollObjects = {
			all: [],
			children: [],
			elements: [],
			groups: [],
			queue: [],
			lastTriggeredObserverEntry: null,
			lastTriggerTime: Date.now()
		};

		this.timing = {
			staggerDelay: 0
		};

		this.vendorPrefixes = {
			animationEnd: {
				animation: 			'animationend',
				MozAnimation: 		'animationend',
				MSAnimation: 		'msAnimationEnd',
				OAnimation: 		'oAnimationEnd',
				WebkitAnimation: 	'webkitAnimationEnd'
			},
			transitionEnd: {
				transition: 		'transitionend',
				MozTransition: 		'transitionend',
				MSTransition: 		'msTransitionEnd',
				OTransition: 		'oTransitionEnd',
				WebkitTransition: 	'webkitTransitionEnd'
			}
		};

		// out of alpha order because this needs vendorPrefixes variable

		this.transitions = {
			animationEnd: this._getVendorPrefixedName('animationEnd'),
			transitionEnd: this._getVendorPrefixedName('transitionEnd')
		};

		// class variable singles

		// let's roll (or not)
		if (this.options.autoEnable) {
			this.enable();
		}
	}

	// Public Methods

	enable() {
		if (!this.flags.isEnabled) {
			this.flags.isEnabled = true;
			this._addEventListeners();

			const states = ['complete', 'loaded', 'interactive'];

			if (states.indexOf(document.readyState) > -1) {
					this.events._onDomReady();
			} else {
				document.addEventListener('DOMContentLoaded', this.events._onDomReady);
			}
		}
	}

	disable() {
		if (this.flags.isEnabled) {
			this.flags.isEnabled = false;
			this._removeEventListeners();
		}
	}

	refresh() {
		this._destroyElements();
		this._createElements();
	}

	refreshContainer(container = document) {
		const scrollElements = container.querySelectorAll(this.options.scrollObjectSelector);

		for (const el of scrollElements) {
			this.add(el);
		}
	}

	add(el) {
		this._createElement(el);
	}

	// Event Handlers

	_onDomReady() {
		this._createElements();
	}

	_onObserverUpdate(entries) {
		for (const entry of entries) {
			this._updateElement(entry);
		}
	}

	_onUpdateScrollObjectQueue() {
		this.updateScrollObjectQueue();
	}

	// Private Methods

	_addEventListeners() {
		if (!this.flags.isListening) {
			this.flags.isListening = true;
			// document.addEventListener('DOMContentLoaded', this.events._onDomReady);
		}
	}

	_removeEventListeners() {
		if (this.flags.isListening) {
			this.flags.isListening = false;
		}
	}

	// Create Element(s)

	_createElements() {
		const scrollElements = document.querySelectorAll(this.options.scrollObjectSelector);

		for (let i = 0; i < scrollElements.length; i++) {
			const $element = scrollElements[i];
			this._createElement($element);
		}
	}

	_createElement(el) {
		if (this.scrollObjects.elements.indexOf(el) === -1) {
			const config = this._createElementConfig(el);
			this.scrollObjects.elements.push(el);
			this.scrollObjects.all.push(config);
			this.observers.intersection.observe(el);
		}
	}

	_createElementConfig(el) {
		const id = this._getUid();
		const children = [];
		const autoShowChildren = el.hasAttribute(this.options.attributeGroup);
		let stagger = el.hasAttribute(this.options.attributeGroup) ? parseFloat(el.getAttribute(this.options.attributeStagger)) : 0;
		if (isNaN(stagger)) { stagger = 0; }

		el.setAttribute(this.options.attributeUniqueId, id);
		el.classList.add(this.options.classInit);

		if (autoShowChildren) {
			const $elChildren = el.querySelectorAll(`*:not([${this.options.attributeGroup}]) ${this.options.scrollObjectChildSelector}`);
			for (let i = 0; i < $elChildren.length; i++) {
				const $child = $elChildren[i];
				children.push(this._createElementConfig($child));
			}
		}

		return {
			el,
			id,
			isFirstShow: true,
			isInViewport: false,
			hideOffscreen: false,
			autoShowChildren,
			children,
			stagger
		};
	}

	// Remove Element(s)

	_destroyElements() {
		const deadScrollElements = this.scrollObjects.all;

		for (const obj of deadScrollElements) {
			obj.el.removeAttribute(this.options.attributeUniqueId);
			this.observers.intersection.unobserve(obj.el);
		}
	}

	// Update Element

	_updateElement(observerEntry) {
		const id = parseInt(observerEntry.target.getAttribute(this.options.attributeUniqueId), 10);
		const scrollObj = this._getScrollObjectReference(id);
		const isVisible = this._checkElementVisibility(observerEntry.target);

		if (!isVisible) {
			return;
		}

		if (observerEntry.intersectionRatio > 0) {
			this._calculateShowDelay(observerEntry, scrollObj);
		} else {
			this._hideElement(scrollObj, observerEntry);
		}
	}

	// Calculate timing based on existing queue and position on screen

	_calculateShowDelay(observerEntry, scrollObj, manualDelay = 0) {
		const now = Date.now();
		const timeDiff = (now - this.scrollObjects.lastTriggerTime) / 1000;

		if (!this.scrollObjects.lastTriggeredObserverEntry) {
			this.scrollObjects.lastTriggeredObserverEntry = observerEntry;
		}

		const horizontalOffset = this._checkHorizontalOffset(observerEntry, this.scrollObjects.lastTriggeredObserverEntry);

		if (manualDelay) {
			this._queueScrollObject(scrollObj, manualDelay);
		} else if (timeDiff < this.options.stagger && horizontalOffset < this.options.maxStaggerElementOffset) {
			// recent item got triggered - keep counting stagger delay
			this.timing.staggerDelay += this.options.stagger;
			this._queueScrollObject(scrollObj, this.timing.staggerDelay + scrollObj.stagger);
		} else {
			// last trigger was long enough ago that stagger delay is reset
			this._showElement(scrollObj, observerEntry);
			this.timing.staggerDelay = scrollObj.stagger;
		}

		if (scrollObj.autoShowChildren && scrollObj.children.length > 0) {
			const delay = this.options.stagger;
			for (let i = 0; i < scrollObj.children.length; i++) {
				const $child = scrollObj.children[i];
				this._calculateShowDelay(observerEntry, $child, delay * (i + 1) + scrollObj.stagger);
			}
		}

		this.options.onElementViewportEnter(scrollObj.el);

		this.scrollObjects.lastTriggerTime = now;
		this.scrollObjects.lastTriggeredObserverEntry = observerEntry;
	}

	// Show & Hide Elements

	_showElement(scrollObj) {
		scrollObj.el.classList.remove(this.options.classOutViewport);
		scrollObj.el.classList.add(this.options.classInViewport);

		if (!scrollObj.isInViewport) {
			scrollObj.isInViewport = true;

			scrollObj.el.classList.remove(this.options.classHide);
			scrollObj.el.classList.add(this.options.classShowStart, this.options.classShowFirst);

			this._transition(scrollObj.el, (el) => {
				el.classList.remove(this.options.classShowStart);
				el.classList.add(this.options.classShow);
			}, (el) => {
				el.classList.remove(this.options.classShow);
				el.classList.add(this.options.classShowComplete);
			});

			scrollObj.el.dispatchEvent(new Event(Skrollex.SHOW));

			this.options.onElementShow(scrollObj.el, scrollObj);
		}
	}

	_hideElement(scrollObj) {
		scrollObj.el.classList.add(this.options.classOutViewport);
		scrollObj.el.classList.remove(this.options.classInViewport, this.options.classShowFirst);

		if (scrollObj.isInViewport) {
			if (scrollObj.hideOffscreen) {
				scrollObj.isInViewport = false;

				scrollObj.el.classList.remove(this.options.classShow);
				scrollObj.el.classList.add(this.options.classHide);

				scrollObj.el.dispatchEvent(new Event(Skrollex.HIDE));

				this.options.onElementHide(scrollObj.el, scrollObj);
			}

			this.options.onElementViewportLeave(scrollObj.el, scrollObj);
		}
	}

	// Manage Queue

	updateScrollObjectQueue() {
		const now = Date.now();

		for (let i = 0; i < this.scrollObjects.queue.length; i++) {
			const queuedElement = this.scrollObjects.queue[i];
			const timeDiff = (now - queuedElement.delayStart) / 1000;

			if (timeDiff >= queuedElement.delay) {
				this._showElement(queuedElement.scrollObj);
				this.scrollObjects.queue.splice(i, 1);
			}
		}

		if (this.scrollObjects.queue.length > 0) {
			requestAnimationFrame(() => {
				this._onUpdateScrollObjectQueue();
			});
		}
	}

	_queueScrollObject(scrollObj, delay) {
		this.scrollObjects.queue.push({
			scrollObj,
			delay,
			delayStart: Date.now()
		});

		scrollObj.el.setAttribute('data-stagger-time', delay);

		this._onUpdateScrollObjectQueue();
	}

	// Destroy

	destroy() {
		this._destroyElements();
		this.disable();
	}

	// Checkers

	_checkElementVisibility(el) {
		return (el.offsetParent !== null);
	}

	_checkHorizontalOffset(a, b) {
		return a.boundingClientRect.top - b.boundingClientRect.top;
	}

	// Getters

	_getUid() {
		return ++this.counters.uid;
	}

	_getScrollObjectReference(id) {
		for (let i = 0; i < this.scrollObjects.all.length; i++) {
			const obj = this.scrollObjects.all[i];
			if (obj.id === id) {
				return obj;
			}
		}
	}

	_getVendorPrefixedName(type = 'animationEnd') {
		for (const t in this.vendorPrefixes[type]) {
			if (this.el.container.style[t] !== undefined) {
				return this.vendorPrefixes[type][t];
			}
		}
	}

	// Utils

	_transition(el, startCallback, completeCallback) {
		const removeEndListener = (_el, _cb) => {
			_el.removeEventListener(this.transitions.animationEnd, _cb);
			_el.removeEventListener(this.transitions.transitionEnd, _cb);
		};

		const addEndListener = (_el, _cb) => {
			removeEndListener(_el, completeCallback);
			_el.addEventListener(this.transitions.animationEnd, _cb);
			_el.addEventListener(this.transitions.transitionEnd, _cb);
		};

		startCallback(el);
		addEndListener(el, () => {
			removeEndListener(el);
			completeCallback(el);
		});
	}

	_merge(...args) {
		const extended = {};
		let deep = false;
		let i = 0;

		// Check if deep merging
		if (Object.prototype.toString.call(args[0]) === '[object Boolean]') {
			[deep] = args;
			i++;
		}

		// Merge the object into the extended object
		const merge = (obj) => {
			for (const prop in obj) {
				if (Object.prototype.hasOwnProperty.call(obj, prop)) {
					// If property is an object, merge properties
					if (deep && Object.prototype.toString.call(obj[prop]) === '[object Object]') {
						extended[prop] = extend(extended[prop], obj[prop]);
					} else {
						extended[prop] = obj[prop];
					}
				}
			}
		};

		// Loop through each object and conduct a merge
		for (; i < args.length; i++) {
			merge(args[i]);
		}

		return extended;
	}

	// const variables

	static get SHOW() {
		return 'show';
	}

	static get HIDE() {
		return 'hide';
	}

	static get VIEWPORT_ENTER() {
		return 'viewportEnter';
	}

	static get VIEWPORT_LEAVE() {
		return 'viewportLeave';
	}

}
